import React, { useCallback, useEffect, useMemo } from 'react';

import { Button, Grid, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCalendar } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { useUpdatePatientMutation } from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';

import { Patient } from '@/types/patient';
import Dropdown from '@/ui/Dropdown';
import LabelText from '@/ui/LabelText';

import styles from '../../../../DeviceScheduleModal/DeviceScheduleModal.module.scss';

import {
  INACTIVE_STATUS_REASONS,
  PATIENT_STATUS_OPTIONS,
  PAUSED_STATUS_REASONS
} from './constants';

interface PatientStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  patientId: string;
  reportId?: number;
  currentStatus?: string;
  currentReason?: string;
  currentStartDate?: Date | string | null;
  currentEndDate?: Date | string | null;
  statusOtherReason?: string | null;
}

interface FormValues {
  status: string | null;
  reason: string | null | undefined;
  otherReason: string | null;
  fromDate: Date | null;
  toDate: Date | null;
}

const parseDate = (value?: Date | string | null): Date | null => (value ? new Date(value) : null);

const PatientStatusModal: React.FC<PatientStatusModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  patientId,
  reportId,
  currentStatus,
  currentReason,
  currentStartDate,
  currentEndDate,
  statusOtherReason
}) => {
  const [updatePatient] = useUpdatePatientMutation();

  const getInitialValues = useCallback<() => FormValues>(() => {
    return {
      status: currentStatus ?? null,
      reason: currentReason ?? null,
      otherReason: statusOtherReason ?? null,
      fromDate: parseDate(currentStartDate),
      toDate: parseDate(currentEndDate)
    };
  }, [currentStatus, currentReason, currentStartDate, currentEndDate, statusOtherReason]);
  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: getInitialValues(),
    mode: 'onChange'
  });

  const status = watch('status');
  const reason = watch('reason');
  const otherReason = watch('otherReason');
  const fromDate = watch('fromDate');

  const showReasonField = status === 'Inactive' || status === 'Paused';
  const showDateFields = status === 'Paused';
  const showOtherReasonField = reason === 'Other' && showReasonField;

  const reasonOptions = useMemo(() => {
    if (status === 'Inactive') return INACTIVE_STATUS_REASONS;
    if (status === 'Paused') return PAUSED_STATUS_REASONS;
    return [];
  }, [status]);

  useEffect(() => {
    if (isOpen) {
      reset(getInitialValues());
    }
  }, [isOpen, getInitialValues, reset]);

  useEffect(() => {
    if (reason !== 'Other') {
      setValue('otherReason', '');
    }
  }, [reason, setValue]);

  const onSubmit = async (data: FormValues) => {
    try {
      const payload: Partial<Patient> = {
        id: patientId,
        patientStatus: data.status ?? undefined,
        statusReason: data.reason,
        statusOtherReason: data.reason === 'Other' ? (data.otherReason ?? '') : '',
        statusStartDate:
          data.status === 'Paused' && data?.fromDate
            ? dayjs(data?.fromDate).format('YYYY-MM-DD')
            : null,
        statusEndDate:
          data.status === 'Paused' && data?.toDate ? dayjs(data?.toDate).format('YYYY-MM-DD') : null
      };

      await updatePatient({
        patient: payload,
        ...(reportId && { activeReportId: reportId })
      }).unwrap();

      notifications.show({
        title: 'Success',
        message: 'Patient status updated successfully.',
        color: 'green'
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error updating patient status:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to update patient status.',
        color: 'red'
      });
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      size="lg"
      withCloseButton={!isSubmitting}
      closeOnClickOutside={false}
      closeOnEscape={!isSubmitting}
      classNames={{
        header: styles.modalHeader,
        body: styles.modalBody
      }}
      title={
        <Text fw={600} size="lg" c="plum.9">
          Edit Patient Status
        </Text>
      }>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={'xs'} mb={8}>
          <Stack gap={'lg'} px={20} pt={16}>
            <div>
              <Text size="xs" fw={600} c="plum.4">
                Status
              </Text>
              <Controller
                control={control}
                name="status"
                rules={{ required: 'Status is required' }}
                render={({ field }) => (
                  <Dropdown
                    options={PATIENT_STATUS_OPTIONS}
                    value={PATIENT_STATUS_OPTIONS.find((o) => o.value === field.value)}
                    onChange={(val) => {
                      field.onChange(val?.value ?? null);
                      setValue('reason', null);
                    }}
                    placeholder="Select status"
                    isDisabled={isSubmitting}
                  />
                )}
              />
              {errors.status && (
                <Text size="xs" c="red" mt={4}>
                  {errors.status.message}
                </Text>
              )}
            </div>

            {showReasonField && (
              <div>
                <Text size="xs" fw={600} c="plum.4">
                  Reason for Status
                </Text>
                <Controller
                  control={control}
                  name="reason"
                  rules={{
                    required: 'Reason for status is required'
                  }}
                  render={({ field }) => (
                    <Dropdown
                      options={reasonOptions.map((r) => ({ label: r, value: r }))}
                      value={field.value ? { label: field.value, value: field.value } : undefined}
                      onChange={(val) => {
                        field.onChange(val?.value ?? null);
                      }}
                      placeholder="Select reason"
                      isDisabled={isSubmitting}
                    />
                  )}
                />
                {errors.reason && (
                  <Text size="xs" c="red" mt={4}>
                    {errors.reason.message}
                  </Text>
                )}
              </div>
            )}

            {showOtherReasonField && (
              <div>
                <Text size="xs" fw={600} c="plum.4">
                  Other Reason
                </Text>
                <Controller
                  control={control}
                  name="otherReason"
                  rules={{
                    required: 'Please provide the other reason',
                    maxLength: {
                      value: 100,
                      message: 'Reason must be 100 characters or less'
                    }
                  }}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      value={field.value || ''}
                      placeholder="Enter reason (max 100 characters)"
                      maxLength={100}
                      disabled={isSubmitting}
                    />
                  )}
                />
                <Text size="xs" c="neutral.6" mt={4}>
                  {otherReason?.length || 0}/100 characters
                </Text>
                {errors.otherReason && (
                  <Text size="xs" c="red" mt={4}>
                    {errors.otherReason.message}
                  </Text>
                )}
              </div>
            )}

            {showDateFields && (
              <div>
                <Text size="xs" fw={600} c="plum.4">
                  Pause Date Range
                </Text>
                <Grid columns={2} gutter="md">
                  <Grid.Col span={1}>
                    <Controller
                      control={control}
                      name="fromDate"
                      rules={{
                        required: 'From date is required'
                      }}
                      render={({ field }) => (
                        <DatePickerInput
                          label={<LabelText label="From Date" size="xs" />}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select from date"
                          rightSection={<IconCalendar size={18} stroke={1.5} />}
                          disabled={isSubmitting}
                          clearable={false}
                          valueFormat="MM/DD/YYYY"
                        />
                      )}
                    />
                    {errors.fromDate && (
                      <Text size="xs" c="red" mt={4}>
                        {errors.fromDate.message}
                      </Text>
                    )}
                  </Grid.Col>
                  <Grid.Col span={1}>
                    <Controller
                      control={control}
                      name="toDate"
                      rules={{
                        required: 'To date is required',
                        validate: (value) => {
                          if (fromDate && value && dayjs(value).isBefore(dayjs(fromDate))) {
                            return 'To date must be after from date';
                          }
                          return true;
                        }
                      }}
                      render={({ field }) => (
                        <DatePickerInput
                          label={<LabelText label="To Date" size="xs" />}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select to date"
                          rightSection={<IconCalendar size={18} stroke={1.5} />}
                          disabled={isSubmitting}
                          clearable={false}
                          valueFormat="MM/DD/YYYY"
                          minDate={fromDate || undefined}
                        />
                      )}
                    />
                    {errors.toDate && (
                      <Text size="xs" c="red" mt={4}>
                        {errors.toDate.message}
                      </Text>
                    )}
                  </Grid.Col>
                </Grid>
              </div>
            )}
          </Stack>

          <Group className={styles.buttonGroup} justify="flex-end">
            <Button
              type="button"
              variant="outline"
              color="plum.9"
              size="xs"
              onClick={handleClose}
              disabled={isSubmitting}
              className={styles.modalButton}>
              Cancel
            </Button>
            <Button
              type="submit"
              color="plum.9"
              size="xs"
              disabled={isSubmitting}
              className={styles.modalButton}>
              Submit
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default PatientStatusModal;
